import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, Users, Search, Filter, Play, CheckCircle } from 'lucide-react';
import {
  Card,
  Badge,
  Input,
  Button,
  ResponsiveDialog,
  Tabs,
  EmptyState,
  useViewport,
  cn,
  formatNaira,
  formatNumber,
  formatDate,
  formatTime,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';
import { mockDraws, getPropertyById } from '../data/mockData';

type DrawStatus = 'all' | 'upcoming' | 'completed';

export const DrawsPage: React.FC = () => {
  const TrophyIcon = ({ className }: { className?: string }) => (
    <Trophy className={className} />
  );
  const { isDesktop, isMobile } = useViewport();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DrawStatus>('all');
  const [selectedDrawId, setSelectedDrawId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Enhance draws with property data
  const draws = useMemo(() => {
    return mockDraws
      .map((draw) => ({
        ...draw,
        property: getPropertyById(draw.propertyId),
      }))
      .filter((draw) => draw.property)
      .sort((a, b) => b.scheduledDate.getTime() - a.scheduledDate.getTime());
  }, []);

  // Apply filters
  const filteredDraws = useMemo(() => {
    return draws.filter((draw) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesProperty = draw.property?.title.toLowerCase().includes(query);
        if (!matchesProperty) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'upcoming' && draw.status !== 'pending') return false;
        if (statusFilter === 'completed' && draw.status !== 'completed') return false;
      }

      return true;
    });
  }, [draws, searchQuery, statusFilter]);

  const selectedDraw = selectedDrawId
    ? draws.find((d) => d.id === selectedDrawId)
    : null;

  // Stats
  const stats = useMemo(() => {
    const total = draws.length;
    const upcoming = draws.filter((d) => d.status === 'pending').length;
    const completed = draws.filter((d) => d.status === 'completed').length;
    const totalPrizeValue = draws
      .filter((d) => d.status === 'completed')
      .reduce((sum, d) => sum + (d.property?.marketValue || 0), 0);
    return { total, upcoming, completed, totalPrizeValue };
  }, [draws]);

  const statusTabs = [
    { value: 'all' as const, label: 'All Draws', count: draws.length },
    { value: 'upcoming' as const, label: 'Upcoming', count: stats.upcoming },
    { value: 'completed' as const, label: 'Completed', count: stats.completed },
  ];

  return (
    <div className="page-container">
      <section className="content-wrapper py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-text-primary mb-2">
              Raffle Draws
            </h1>
            <p className="text-text-secondary">
              View upcoming draws and past results
            </p>
          </div>
        </div>

        {/* Stats Cards (Desktop) */}
        {isDesktop && (
          <motion.div
            className="grid grid-cols-4 gap-4 mb-8"
            variants={staggerChildren}
            initial="hidden"
            animate="show"
          >
            {[
              { label: 'Total Draws', value: stats.total, icon: Trophy, color: 'text-gold-500' },
              { label: 'Upcoming', value: stats.upcoming, icon: Calendar, color: 'text-blue-400' },
              { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-green-400' },
              { label: 'Total Prizes Awarded', value: formatNaira(stats.totalPrizeValue), icon: Users, color: 'text-purple-400', small: true },
            ].map((stat, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-muted mb-1">{stat.label}</p>
                      <p className={cn(
                        'font-mono font-bold text-text-primary tabular-nums',
                        stat.small ? 'text-lg' : 'text-2xl'
                      )}>
                        {stat.value}
                      </p>
                    </div>
                    <stat.icon className={cn('w-5 h-5', stat.color)} />
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              type="search"
              placeholder="Search draws by property..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
            />
          </div>
          
          {isMobile && (
            <Button variant="outline" onClick={() => setShowFilters(true)}>
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
          )}
        </div>

        {/* Status Tabs */}
        <Tabs
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as DrawStatus)}
          tabs={statusTabs.map((tab) => ({
            value: tab.value,
            label: tab.label,
            badge: tab.count,
          }))}
          className="mb-6"
        />

        {/* Draws List */}
        {filteredDraws.length === 0 ? (
          <EmptyState
            icon={TrophyIcon}
            title="No draws found"
            description={
              searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'No raffle draws have been scheduled yet'
            }
            action={
              searchQuery || statusFilter !== 'all'
                ? {
                    label: 'Clear Filters',
                    onClick: () => {
                      setSearchQuery('');
                      setStatusFilter('all');
                    },
                  }
                : undefined
            }
          />
        ) : (
          <motion.div
            className="space-y-4"
            variants={staggerChildren}
            initial="hidden"
            animate="show"
          >
            {filteredDraws.map((draw) => {
              const property = draw.property;
              if (!property) return null;

              const isUpcoming = draw.status === 'pending';
              const isPast = draw.status === 'completed';

              return (
                <motion.div key={draw.id} variants={fadeInUp}>
                  <Card
                    className="overflow-hidden cursor-pointer hover:border-gold-500/30 transition-colors"
                    onClick={() => setSelectedDrawId(draw.id)}
                  >
                    <div className={cn(
                      'flex gap-4 p-4',
                      isMobile ? 'flex-col' : ''
                    )}>
                      {/* Property Image */}
                      <div className={cn(
                        'flex-shrink-0 rounded-vault-lg overflow-hidden',
                        isMobile ? 'w-full h-40' : 'w-48 h-32'
                      )}>
                        <img
                          src={property.images[0]}
                          alt={property.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <Badge
                              variant={isUpcoming ? 'warning' : 'success'}
                              className="mb-2"
                            >
                              {isUpcoming ? 'Upcoming' : 'Completed'}
                            </Badge>
                            <h3 className="font-display text-lg font-semibold text-text-primary">
                              {property.title}
                            </h3>
                            <p className="text-sm text-text-muted">{property.location}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div>
                            <p className="text-xs text-text-muted">Property Value</p>
                            <p className="font-mono font-semibold text-text-primary tabular-nums">
                              {formatNaira(property.marketValue)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Tickets Sold</p>
                            <p className="font-mono font-semibold text-text-primary tabular-nums">
                              {formatNumber(property.ticketsSold)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Draw Date</p>
                            <p className="font-mono font-semibold text-text-primary tabular-nums">
                              {formatDate(draw.scheduledDate)}
                            </p>
                          </div>
                          {isPast && draw.winnerTicketNumber && (
                            <div>
                              <p className="text-xs text-text-muted">Winner</p>
                              <p className="font-mono font-semibold text-gold-500 tabular-nums">
                                #{draw.winnerTicketNumber}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action indicator */}
                      {isDesktop && (
                        <div className="flex items-center">
                          {isUpcoming ? (
                            <div className="p-3 rounded-full bg-gold-500/10">
                              <Play className="w-5 h-5 text-gold-500" />
                            </div>
                          ) : (
                            <div className="p-3 rounded-full bg-green-500/10">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </section>

      {/* Draw Detail Modal */}
      <ResponsiveDialog
        isOpen={!!selectedDraw}
        onClose={() => setSelectedDrawId(null)}
        title="Draw Details"
      >
        {selectedDraw && (
          <div className="p-6 space-y-6">
            {/* Property Info */}
            <div className="rounded-vault-lg overflow-hidden">
              <img
                src={selectedDraw.property?.images[0]}
                alt={selectedDraw.property?.title}
                className="w-full h-48 object-cover"
              />
            </div>

            <div>
              <Badge
                variant={selectedDraw.status === 'pending' ? 'warning' : 'success'}
                className="mb-2"
              >
                {selectedDraw.status === 'pending' ? 'Upcoming Draw' : 'Completed'}
              </Badge>
              <h3 className="font-display text-xl font-bold text-text-primary">
                {selectedDraw.property?.title}
              </h3>
              <p className="text-sm text-text-muted mt-1">
                {selectedDraw.property?.location}
              </p>
            </div>

            {/* Draw Info */}
            <div className="space-y-3">
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Property Value</span>
                <span className="font-mono font-semibold text-text-primary tabular-nums">
                  {formatNaira(selectedDraw.property?.marketValue || 0)}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Tickets Sold</span>
                <span className="font-mono font-semibold text-text-primary tabular-nums">
                  {formatNumber(selectedDraw.property?.ticketsSold || 0)}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-text-muted">Draw Date</span>
                <span className="text-text-primary">
                  {formatDate(selectedDraw.scheduledDate)} at {formatTime(selectedDraw.scheduledDate)}
                </span>
              </div>
              {selectedDraw.status === 'completed' && (
                <>
                  {selectedDraw.winnerTicketNumber && (
                    <div className="flex justify-between py-3 border-b border-white/5">
                      <span className="text-text-muted">Winning Ticket</span>
                      <span className="font-mono font-bold text-gold-500 tabular-nums">
                        #{selectedDraw.winnerTicketNumber}
                      </span>
                    </div>
                  )}
                  {selectedDraw.executedAt && (
                    <div className="flex justify-between py-3">
                      <span className="text-text-muted">Draw Executed</span>
                      <span className="text-text-primary">
                        {formatDate(selectedDraw.executedAt)} at {formatTime(selectedDraw.executedAt)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Winner announcement for completed draws */}
            {selectedDraw.status === 'completed' && selectedDraw.winnerTicketNumber && (
              <div className="p-6 rounded-vault-lg bg-gradient-to-br from-gold-500/20 to-gold-500/5 border border-gold-500/30 text-center">
                <Trophy className="w-12 h-12 text-gold-500 mx-auto mb-3" />
                <p className="font-display text-lg font-bold text-gold-500 mb-1">
                  Congratulations to the Winner!
                </p>
                <p className="text-sm text-text-secondary">
                  Ticket #{selectedDraw.winnerTicketNumber} has won this property
                </p>
              </div>
            )}

            <Button variant="outline" fullWidth onClick={() => setSelectedDrawId(null)}>
              Close
            </Button>
          </div>
        )}
      </ResponsiveDialog>

      {/* Mobile Filter Dialog */}
      <ResponsiveDialog
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter Draws"
      >
        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-medium text-text-primary">Status</p>
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => {
                  setStatusFilter(tab.value);
                  setShowFilters(false);
                }}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-vault border transition-colors',
                  statusFilter === tab.value
                    ? 'bg-gold-500/10 border-gold-500/30 text-gold-500'
                    : 'bg-surface-2/50 border-white/5 text-text-secondary hover:border-white/10'
                )}
              >
                <span>{tab.label}</span>
                <span className="font-mono text-sm tabular-nums">{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
